/**
 * Specialized standalone Bucket for Daily Update Attachments (fac_daily_update_attach table)
 * Manages its own IndexedDB instance specifically for this table.
 *
 * --- SCHEMA DEFINITION ---
 * @typedef {Object} FacDailyUpdateAttach
 * @property {string} id - Primary Key (String)
 * @property {number} comm_id - Community ID (Number)
 * @property {number} user_id - User ID (Number)
 * @property {string} user_name - User Display Name (String)
 * @property {string} c_date - Date in YYYY-MM-DD format (String)
 * @property {string} file_name - File name with slug/extension (String)
 * @property {string} file_url - Blob URL or S3 URL (String)
 * @property {number} attach_cat_id - Category ID (Number)
 * @property {boolean} is_updated_by_sv_user - Status flag (Boolean)
 * @property {number} is_app - Approved flag: 1 or 0 (Number)
 * @property {string} operation - Operation to sync: 'add', 'update', 'delete'. If null/undefined, item is synced.
 * @property {string} [notes] - Textual notes (String)
 * @property {Blob|string|null} image - The actual image binary or base64 (Blob/String)
 * @property {string} timestamp - ISO timestamp of local addition (String)
 * @property {string} type - Table identifier 'fac_daily_update_attach' (String)
 */
class DailyUpdateBucket {
  constructor () {
    this.dbName = 'integra_offline_db'
    this.storeName = 'fac_daily_update_attach'
    this.version = 1
    /** @type {IDBDatabase|null} */
    this.db = null
  }

  /**
   * Initializes the database connection for this specific store
   * @returns {Promise<IDBDatabase>}
   */
  async init () {
    if (this.db) return this.db
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version)
      request.onupgradeneeded = (e) => {
        const db = e.target.result
        if (!db.objectStoreNames.contains(this.storeName)) {
          // Creating the store with ID and an index for images if needed
          db.createObjectStore(this.storeName, { keyPath: 'id' })
        }
      }
      request.onsuccess = (e) => {
        this.db = e.target.result
        resolve(this.db)
      }
      request.onerror = (e) => reject(e)
    })
  }

  /**
   * Adds a new record with the dedicated 'image' column (field)
   * @param {FacDailyUpdateAttach} photoData
   * @returns {Promise<string>}
   */
  async addPhoto (photoData) {
    /** @type {IDBDatabase} */
    const db = await this.init()
    const id = photoData.id || Date.now().toString()

    const record = {
      ...photoData,
      id: id,
      type: 'fac_daily_update_attach',
      image: photoData.image || null,
      operation: 'add',
      timestamp: new Date().toISOString()
    }

    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.storeName], 'readwrite')
      const store = tx.objectStore(this.storeName)
      const request = store.add(record)
      request.onsuccess = () => resolve(id)
      request.onerror = reject
    })
  }

  /**
   * Updates an existing record with the dedicated 'image' column (field)
   * @param {FacDailyUpdateAttach} record
   * @returns {Promise<string>}
   */
  async updatePhoto (record) {
    /** @type {IDBDatabase} */
    const db = await this.init()
    const updatedRecord = { ...record }
    if (updatedRecord.operation !== 'add') {
      updatedRecord.operation = 'update'
    }
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.storeName], 'readwrite')
      const store = tx.objectStore(this.storeName)
      const request = store.put(updatedRecord)
      request.onsuccess = () => resolve(updatedRecord.id)
      request.onerror = reject
    })
  }

  /**
   * Bulk saves a list of photos (for caching server data)
   * @param {FacDailyUpdateAttach[]} photos
   * @returns {Promise<void>}
   */
  async savePhotos (photos) {
    if (!photos || photos.length === 0) return
    /** @type {IDBDatabase} */
    const db = await this.init()
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.storeName], 'readwrite')
      const store = tx.objectStore(this.storeName)
      photos.forEach(photo => {
        const syncedPhoto = { ...photo }
        delete syncedPhoto.operation
        store.put(syncedPhoto)
      })
      tx.oncomplete = () => resolve()
      tx.onerror = (e) => reject(e)
    })
  }

  /**
   * Retrieves all offline/cached photos
   * @returns {Promise<FacDailyUpdateAttach[]>}
   */
  async getAllPhotos () {
    /** @type {IDBDatabase} */
    const db = await this.init()
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.storeName], 'readonly')
      const store = tx.objectStore(this.storeName)
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = reject
    })
  }

  /**
   * Deletes a photo from the offline bucket by ID
   */
  async deletePhoto (id) {
    /** @type {IDBDatabase} */
    const db = await this.init()
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.storeName], 'readwrite')
      const store = tx.objectStore(this.storeName)
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = reject
    })
  }
}

export default new DailyUpdateBucket()



// WEBPACK FOOTER //
// ./src/utils/DailyUpdateBucket.js